import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  CheckCircle,
  DeleteOutline,
  Link as LinkIcon,
  PhoneAndroid,
  Save,
  Send,
  WhatsApp
} from '@mui/icons-material';
import AcessoRestrito from '../../components/AcessoRestrito';
import api from '../../services/api';

const STATUS = {
  desconectado: { label: 'Desconectado', color: 'default' },
  iniciando: { label: 'Iniciando', color: 'info' },
  aguardando_qr: { label: 'Aguardando leitura do QR Code', color: 'warning' },
  autenticando: { label: 'Concluindo conexão', color: 'info' },
  conectado: { label: 'Conectado', color: 'success' },
  sessao_encerrada: { label: 'Sessão encerrada', color: 'default' },
  erro: { label: 'Erro de conexão', color: 'error' }
};

const estadoInicial = {
  status: 'desconectado',
  qrCode: null,
  numero: null,
  nome: null,
  versaoBiblioteca: null,
  versaoProtocolo: null,
  protocoloAtual: null,
  conectadoEm: null,
  ultimaAtividade: null,
  ultimoErro: null,
  codigoDesconexao: null
};

const formatarDataHora = (valor) => valor
  ? new Date(valor).toLocaleString('pt-BR')
  : '—';

const VARIAVEIS_AGENDAMENTO = [
  '[nome_da_empresa]',
  '[nome_do_paciente]',
  '[data_do_agendamento]',
  '[horario_do_agendamento]',
  '[nome_do_servico]',
  '[nome_do_colaborador]',
  '[cargo_do_colaborador]',
  '[local_do_atendimento]'
];

const MENSAGENS_EXEMPLO = {
  novo_agendamento_colaborador: '*[nome_da_empresa]*\n\nPrezado(a) [nome_do_colaborador],\n\nInformamos que um novo atendimento foi incluído em sua agenda.\n\nData: [data_do_agendamento]\nHorário: [horario_do_agendamento]\nServiço: [nome_do_servico]\nPaciente: [nome_do_paciente]\nLocal: [local_do_atendimento]',
  confirmacao_agendamento_paciente: '*[nome_da_empresa]*\n\nPrezado(a) [nome_do_paciente],\n\nAgradecemos por escolher a [nome_da_empresa]. Seu atendimento foi agendado com sucesso.\n\nData: [data_do_agendamento]\nHorário: [horario_do_agendamento]\nServiço: [nome_do_servico]\nProfissional: [nome_do_colaborador]\nLocal: [local_do_atendimento]\n\nSerá um prazer recebê-lo(a). Em caso de dúvidas ou necessidade de alteração, entre em contato conosco.',
  cancelamento_colaborador: '*[nome_da_empresa]*\n\nPrezado(a) [nome_do_colaborador],\n\nInformamos que o atendimento abaixo foi cancelado.\n\nData: [data_do_agendamento]\nHorário: [horario_do_agendamento]\nServiço: [nome_do_servico]\nPaciente: [nome_do_paciente]\nLocal: [local_do_atendimento]',
  agendamento_hoje_paciente: '*[nome_da_empresa]*\n\nPrezado(a) [nome_do_paciente],\n\nLembramos que seu atendimento está agendado para hoje.\n\nHorário: [horario_do_agendamento]\nServiço: [nome_do_servico]\nProfissional: [nome_do_colaborador]\nLocal: [local_do_atendimento]',
  agendamento_amanha_paciente: '*[nome_da_empresa]*\n\nPrezado(a) [nome_do_paciente],\n\nEste é um lembrete de que seu atendimento está agendado para amanhã.\n\nData: [data_do_agendamento]\nHorário: [horario_do_agendamento]\nServiço: [nome_do_servico]\nProfissional: [nome_do_colaborador]\nLocal: [local_do_atendimento]'
  ,
  agendamento_data_paciente: '*[nome_da_empresa]*\n\nPrezado(a) [nome_do_paciente],\n\nGostaríamos de lembrá-lo(a) de seu próximo atendimento.\n\nData: [data_do_agendamento]\nHorário: [horario_do_agendamento]\nServiço: [nome_do_servico]\nProfissional: [nome_do_colaborador]\nLocal: [local_do_atendimento]'
};

const mesclarModalidadesComPadroes = (recebidas = {}) => Object.fromEntries(
  Object.entries(MENSAGENS_EXEMPLO).map(([chave, mensagemPadrao]) => {
    const recebida = recebidas?.[chave] || {};
    const mensagemRecebida = typeof recebida.mensagem === 'string'
      ? recebida.mensagem.trim()
      : '';
    return [chave, {
      ativo: recebida.ativo === true,
      mensagem: mensagemRecebida || mensagemPadrao,
      ultimoEnvioEm: recebida.ultimoEnvioEm || null,
      ultimaDataReferencia: recebida.ultimaDataReferencia || null,
      ultimoResultado: recebida.ultimoResultado || null
    }];
  })
);

const MODALIDADES = [
  {
    chave: 'novo_agendamento_colaborador',
    rotulo: 'Avisar o colaborador sobre um novo agendamento',
    variaveis: [...VARIAVEIS_AGENDAMENTO, '[idade_do_paciente]']
  },
  {
    chave: 'confirmacao_agendamento_paciente',
    rotulo: 'Confirmar imediatamente o novo agendamento ao paciente',
    variaveis: VARIAVEIS_AGENDAMENTO
  },
  {
    chave: 'cancelamento_colaborador',
    rotulo: 'Avisar o colaborador sobre um cancelamento',
    variaveis: VARIAVEIS_AGENDAMENTO
  },
  {
    chave: 'agendamento_hoje_paciente',
    rotulo: 'Avisar o paciente sobre um agendamento hoje',
    variaveis: VARIAVEIS_AGENDAMENTO,
    manual: true,
    periodo: 'hoje'
  },
  {
    chave: 'agendamento_amanha_paciente',
    rotulo: 'Avisar o paciente sobre um agendamento amanhã',
    variaveis: VARIAVEIS_AGENDAMENTO,
    manual: true,
    periodo: 'amanha'
  },
  {
    chave: 'agendamento_data_paciente',
    rotulo: 'Avisar pacientes com agendamento em uma data específica',
    variaveis: VARIAVEIS_AGENDAMENTO,
    manual: true,
    selecionarData: true
  }
];

export default function ModuloWhatsApp() {
  const [conexao, setConexao] = useState(estadoInicial);
  const [carregando, setCarregando] = useState(true);
  const [acao, setAcao] = useState('');
  const [numeroTeste, setNumeroTeste] = useState('');
  const [mensagemTeste, setMensagemTeste] = useState('Teste de conexão do AuthSystem.');
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [modalidades, setModalidades] = useState(() => mesclarModalidadesComPadroes());
  const [dataDisparo, setDataDisparo] = useState('');
  const [aceiteUsoTerceiros, setAceiteUsoTerceiros] = useState(false);
  const [aceiteUsoTerceirosEm, setAceiteUsoTerceirosEm] = useState(null);
  const [confirmarAceiteAberto, setConfirmarAceiteAberto] = useState(false);

  const carregarStatus = useCallback(async ({ silencioso = false } = {}) => {
    try {
      if (!silencioso) setCarregando(true);
      const response = await api.get('/whatsapp/status');
      setConexao({ ...estadoInicial, ...response.data });
    } catch (error) {
      if (!silencioso) {
        setErro(error.response?.data?.message || 'Não foi possível consultar o WhatsApp.');
      }
    } finally {
      if (!silencioso) setCarregando(false);
    }
  }, []);

  const carregarConfiguracoes = useCallback(async () => {
    try {
      const response = await api.get('/whatsapp/configuracoes');
      setModalidades(mesclarModalidadesComPadroes(response.data?.modalidades));
      setAceiteUsoTerceiros(response.data?.aceiteUsoTerceiros === true);
      setAceiteUsoTerceirosEm(response.data?.aceiteUsoTerceirosEm || null);
    } catch (error) {
      setErro(error.response?.data?.message || 'Não foi possível carregar as configurações.');
    }
  }, []);

  useEffect(() => {
    carregarStatus();
    carregarConfiguracoes();
    const intervalo = window.setInterval(
      () => carregarStatus({ silencioso: true }),
      2500
    );
    return () => window.clearInterval(intervalo);
  }, [carregarStatus, carregarConfiguracoes]);

  const alterarModalidade = (chave, campo, valor) => {
    setModalidades((prev) => ({
      ...prev,
      [chave]: { ...prev[chave], [campo]: valor }
    }));
  };

  const salvarConfiguracoes = async () => {
    if (MODALIDADES.some(
      ({ chave }) => modalidades[chave]?.ativo && !modalidades[chave]?.mensagem?.trim()
    )) {
      setErro('Informe a mensagem de todas as notificações ativadas.');
      return;
    }

    try {
      setAcao('salvar_configuracoes');
      setErro('');
      const response = await api.put('/whatsapp/configuracoes', {
        modalidades,
        aceiteUsoTerceiros
      });
      setModalidades(mesclarModalidadesComPadroes(response.data?.modalidades || modalidades));
      setSucesso(response.data?.message || 'Configurações salvas.');
    } catch (error) {
      setErro(error.response?.data?.message || 'Não foi possível salvar as configurações.');
    } finally {
      setAcao('');
    }
  };

  const alterarAceiteUsoTerceiros = async (aceito) => {
    if (aceito) {
      setConfirmarAceiteAberto(true);
      return;
    }

    const valorAnterior = aceiteUsoTerceiros;
    setAceiteUsoTerceiros(false);
    try {
      setAcao('salvar_aceite');
      const response = await api.put('/whatsapp/configuracoes', {
        modalidades,
        aceiteUsoTerceiros: false
      });
      setAceiteUsoTerceiros(response.data?.aceiteUsoTerceiros === true);
      setAceiteUsoTerceirosEm(response.data?.aceiteUsoTerceirosEm || null);
      setSucesso('Ciência sobre o uso do WhatsApp removida.');
    } catch (error) {
      setAceiteUsoTerceiros(valorAnterior);
      setErro(error.response?.data?.message || 'Não foi possível registrar a ciência.');
    } finally {
      setAcao('');
    }
  };

  const confirmarAceiteUsoTerceiros = async () => {
    try {
      setAcao('salvar_aceite');
      setErro('');
      const response = await api.post('/whatsapp/consentimento', {
        contextoNavegador: {
          fusoHorario: Intl.DateTimeFormat().resolvedOptions().timeZone,
          idioma: navigator.language,
          idiomas: navigator.languages,
          plataforma: navigator.userAgentData?.platform || navigator.platform,
          larguraTela: window.screen?.width,
          alturaTela: window.screen?.height,
          profundidadeCor: window.screen?.colorDepth,
          larguraJanela: window.innerWidth,
          alturaJanela: window.innerHeight
        }
      });
      setAceiteUsoTerceiros(response.data?.aceiteUsoTerceiros === true);
      setAceiteUsoTerceirosEm(response.data?.aceiteUsoTerceirosEm || null);
      setConfirmarAceiteAberto(false);
      if (response.data?.emailAuditoriaEnviado === false) {
        setErro(response.data?.message);
      } else {
        setSucesso(response.data?.message || 'Ciência registrada e comunicada à SAOTech Softwares.');
      }
    } catch (error) {
      setAceiteUsoTerceiros(false);
      setErro(error.response?.data?.message || 'Não foi possível registrar e comunicar a ciência.');
    } finally {
      setAcao('');
    }
  };

  const dispararLembretes = async (modalidade, periodo, selecionarData) => {
    if (!conectado) {
      setErro('Conecte o WhatsApp antes de realizar o disparo.');
      return;
    }
    if (selecionarData && !dataDisparo) {
      setErro('Selecione a data dos agendamentos que deverão receber a mensagem.');
      return;
    }

    try {
      setAcao(`disparar_${modalidade}`);
      setErro('');
      await api.put('/whatsapp/configuracoes', { modalidades, aceiteUsoTerceiros });
      const response = await api.post('/whatsapp/notificacoes/agendamentos', {
        modalidade,
        periodo,
        data: selecionarData ? dataDisparo : undefined
      });
      await carregarConfiguracoes();
      setSucesso(response.data?.message || 'Disparo concluído.');
    } catch (error) {
      setErro(error.response?.data?.message || 'Não foi possível realizar o disparo.');
    } finally {
      setAcao('');
    }
  };

  const conectar = async () => {
    try {
      setAcao('conectar');
      setErro('');
      const response = await api.post('/whatsapp/conectar');
      setConexao({ ...estadoInicial, ...response.data });
      setSucesso('Conexão iniciada. Aguarde a geração do QR Code.');
    } catch (error) {
      setErro(error.response?.data?.message || 'Não foi possível iniciar a conexão.');
    } finally {
      setAcao('');
    }
  };

  const testar = async () => {
    if (!numeroTeste.trim() || !mensagemTeste.trim()) {
      setErro('Número com DDI/DDD e mensagem de teste são obrigatórios.');
      return;
    }

    try {
      setAcao('testar');
      setErro('');
      const response = await api.post('/whatsapp/testar', {
        numero: numeroTeste,
        mensagem: mensagemTeste
      });
      setSucesso(response.data?.message || 'Mensagem de teste enviada.');
      carregarStatus({ silencioso: true });
    } catch (error) {
      setErro(error.response?.data?.message || 'Não foi possível enviar a mensagem de teste.');
    } finally {
      setAcao('');
    }
  };

  const encerrar = async () => {
    const confirmado = window.confirm(
      'Encerrar a sessão removerá o aparelho vinculado e exigirá um novo QR Code. Deseja continuar?'
    );
    if (!confirmado) return;

    try {
      setAcao('encerrar');
      setErro('');
      const response = await api.delete('/whatsapp/sessao');
      setConexao({ ...estadoInicial, ...response.data });
      setSucesso('Sessão do WhatsApp encerrada.');
    } catch (error) {
      setErro(error.response?.data?.message || 'Não foi possível encerrar a sessão.');
    } finally {
      setAcao('');
    }
  };

  const status = STATUS[conexao.status] || STATUS.desconectado;
  const conectado = conexao.status === 'conectado';

  return (
    <AcessoRestrito modulo="whatsapp">
      <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          mb={3}
        >
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <WhatsApp sx={{ color: '#25D366', fontSize: 36 }} />
              <Typography variant="h4">WhatsApp</Typography>
            </Stack>
            <Typography color="text.secondary" mt={0.5}>
              Conexão do aparelho e teste de envio do AuthSystem
            </Typography>
          </Box>
          <Chip
            icon={conectado ? <CheckCircle /> : <PhoneAndroid />}
            label={status.label}
            color={status.color}
            variant={conectado ? 'filled' : 'outlined'}
          />
        </Stack>

        {conexao.ultimoErro && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {conexao.ultimoErro}
          </Alert>
        )}

        {carregando ? (
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 5fr) minmax(0, 7fr)' },
              gap: 3,
              alignItems: 'stretch'
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6">Conexão do aparelho</Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  No celular, abra WhatsApp → Aparelhos conectados → Conectar aparelho.
                </Typography>

                <Box
                  sx={{
                    minHeight: 350,
                    my: 3,
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default'
                  }}
                >
                  {conexao.qrCode ? (
                    <Box textAlign="center" p={2}>
                      <Box
                        component="img"
                        src={conexao.qrCode}
                        alt="QR Code para conectar o WhatsApp"
                        sx={{ width: 300, maxWidth: '100%', display: 'block' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        O QR Code é temporário e será renovado automaticamente.
                      </Typography>
                    </Box>
                  ) : conectado ? (
                    <Stack alignItems="center" spacing={1.5}>
                      <CheckCircle sx={{ color: 'success.main', fontSize: 72 }} />
                      <Typography variant="h6">Aparelho conectado</Typography>
                      <Typography color="text.secondary">
                        {conexao.nome || 'WhatsApp'} {conexao.numero ? `• +${conexao.numero}` : ''}
                      </Typography>
                    </Stack>
                  ) : ['iniciando', 'autenticando'].includes(conexao.status) ? (
                    <Stack alignItems="center" spacing={2}>
                      <CircularProgress />
                      <Typography>
                        {conexao.status === 'autenticando'
                          ? 'QR Code lido. Concluindo conexão...'
                          : 'Preparando conexão...'}
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack alignItems="center" spacing={2} textAlign="center" px={3}>
                      <PhoneAndroid sx={{ color: 'text.secondary', fontSize: 64 }} />
                      <Typography color="text.secondary">
                        Inicie a conexão para gerar um QR Code.
                      </Typography>
                    </Stack>
                  )}
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    variant="contained"
                    startIcon={<LinkIcon />}
                    onClick={conectar}
                    disabled={acao !== '' ||
                      !aceiteUsoTerceiros ||
                      ['iniciando', 'aguardando_qr', 'autenticando', 'conectado'].includes(conexao.status)}
                    fullWidth
                  >
                    {acao === 'conectar' ? <CircularProgress size={22} /> :
                      'Conectar'}
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteOutline />}
                    onClick={encerrar}
                    disabled={!aceiteUsoTerceiros || acao !== '' || conexao.status === 'desconectado'}
                    fullWidth
                  >
                    Encerrar sessão
                  </Button>
                </Stack>
              </Paper>
            </Box>

            <Box sx={{ display: 'flex', minWidth: 0 }}>
              <Stack spacing={3} sx={{ width: '100%', height: '100%' }}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6">Aviso sobre o uso do WhatsApp</Typography>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2" paragraph>
                      Esta aplicação utiliza uma integração desenvolvida com uma biblioteca de
                      terceiros para comunicação via WhatsApp. O uso dessa tecnologia não é ilegal,
                      porém <strong>não se trata de uma solução oficial fornecida ou homologada pela
                      Meta (WhatsApp)</strong>, estando sujeita a limitações e alterações da plataforma.
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Por esse motivo, podem ocorrer desconexões, necessidade de nova autenticação e
                      interrupções no funcionamento. Além disso, caso o número realize envios em grande
                      volume, mensagens repetitivas ou apresente comportamentos que o WhatsApp
                      identifique como automação abusiva ou spam, a própria plataforma poderá aplicar
                      restrições, incluindo o bloqueio temporário ou permanente do número.
                    </Typography>
                    <Typography variant="body2">
                      Ao utilizar esta funcionalidade, você declara estar ciente dessas informações e
                      dos riscos inerentes ao uso de uma integração de terceiros, comprometendo-se a
                      utilizar o recurso de forma responsável e em conformidade com as políticas do
                      WhatsApp.
                    </Typography>
                  </Alert>
                  <FormControlLabel
                    sx={{ mt: 2, alignItems: 'flex-start' }}
                    control={
                      <Checkbox
                        checked={aceiteUsoTerceiros}
                        onChange={(event) => alterarAceiteUsoTerceiros(event.target.checked)}
                        disabled={acao !== ''}
                      />
                    }
                    label={
                      <Box pt={0.75}>
                        <Typography fontWeight={600}>
                          Declaro que li e estou ciente das informações e dos riscos apresentados.
                        </Typography>
                        {aceiteUsoTerceirosEm && (
                          <Typography variant="caption" color="text.secondary">
                            Ciência registrada em {formatarDataHora(aceiteUsoTerceirosEm)}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </Paper>
              </Stack>
            </Box>

            <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
              <Stack spacing={3}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6">Notificações automáticas</Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    Ative os avisos desejados e personalize a mensagem de cada modalidade.
                  </Typography>
                  <Alert severity="info" sx={{ my: 2 }}>
                    As preferências serão salvas agora. O disparo pelos agendamentos será integrado posteriormente.
                  </Alert>

                  <Stack spacing={2.5}>
                    {MODALIDADES.map(({
                      chave, rotulo, variaveis, manual, periodo, selecionarData
                    }) => {
                      const configuracao = modalidades[chave] || { ativo: false, mensagem: '' };
                      return (
                        <Box key={chave}>
                          {manual ? (
                            <Typography variant="subtitle2">{rotulo}</Typography>
                          ) : (
                            <FormControlLabel
                              control={
                                <Switch
                                checked={Boolean(configuracao.ativo)}
                                disabled={!aceiteUsoTerceiros}
                                  onChange={(event) =>
                                    alterarModalidade(chave, 'ativo', event.target.checked)}
                                />
                              }
                              label={rotulo}
                            />
                          )}
                          <TextField
                            label="Mensagem"
                            value={configuracao.mensagem}
                            onChange={(event) =>
                              alterarModalidade(chave, 'mensagem', event.target.value)}
                            required={configuracao.ativo}
                            disabled={!aceiteUsoTerceiros}
                            error={configuracao.ativo && !configuracao.mensagem?.trim()}
                            helperText={`${configuracao.mensagem?.length || 0}/1000 caracteres`}
                            inputProps={{ maxLength: 1000 }}
                            multiline
                            minRows={3}
                            fullWidth
                            sx={{ mt: 1 }}
                          />
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Variáveis disponíveis:
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={0.75} mt={0.75}>
                              {variaveis.map((variavel) => (
                                <Chip
                                  key={variavel}
                                  label={variavel}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Stack>
                          </Box>
                          {manual && (
                            <>
                              <Alert severity="info" sx={{ mt: 1.5 }}>
                                Último envio: {formatarDataHora(configuracao.ultimoEnvioEm)}
                                {configuracao.ultimaDataReferencia &&
                                  ` • Agendamentos de ${new Date(
                                    `${configuracao.ultimaDataReferencia}T12:00:00`
                                  ).toLocaleDateString('pt-BR')}`}
                                {configuracao.ultimoResultado &&
                                  ` • ${configuracao.ultimoResultado.enviados || 0} enviada(s)`}
                              </Alert>
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.5}
                                mt={1.5}
                                justifyContent="flex-end"
                              >
                                {selecionarData && (
                                  <TextField
                                    type="date"
                                    label="Data dos agendamentos *"
                                    value={dataDisparo}
                                    onChange={(event) => setDataDisparo(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!aceiteUsoTerceiros || acao !== ''}
                                    sx={{ minWidth: 220 }}
                                  />
                                )}
                                <Button
                                  variant="outlined"
                                  color="success"
                                  startIcon={<Send />}
                                  onClick={() =>
                                    dispararLembretes(chave, periodo, selecionarData)}
                                  disabled={!aceiteUsoTerceiros || !conectado || acao !== ''}
                                >
                                  {acao === `disparar_${chave}`
                                    ? <CircularProgress size={22} color="inherit" />
                                    : 'Enviar agora'}
                                </Button>
                              </Stack>
                            </>
                          )}
                        </Box>
                      );
                    })}
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={salvarConfiguracoes}
                      disabled={!aceiteUsoTerceiros || acao !== ''}
                      sx={{ alignSelf: { md: 'flex-end' }, minWidth: 220 }}
                    >
                      {acao === 'salvar_configuracoes'
                        ? <CircularProgress size={22} color="inherit" />
                        : 'Salvar configurações'}
                    </Button>
                  </Stack>
                </Paper>

                <Card>
                  <CardContent>
                    <Typography variant="h6" mb={2}>Informações da conexão</Typography>
                    <Grid container spacing={2}>
                      {[
                        ['Estado', status.label],
                        ['Número', conexao.numero ? `+${conexao.numero}` : '—'],
                        ['Nome do perfil', conexao.nome || '—'],
                        ['Baileys', conexao.versaoBiblioteca || '—'],
                        ['Protocolo WhatsApp Web', conexao.versaoProtocolo || '—'],
                        ['Protocolo atual', conexao.protocoloAtual === null ? 'Não verificado' :
                          conexao.protocoloAtual ? 'Sim' : 'Não'],
                        ['Código da desconexão', conexao.codigoDesconexao || '—'],
                        ['Conectado em', formatarDataHora(conexao.conectadoEm)],
                        ['Última atividade', formatarDataHora(conexao.ultimaAtividade)]
                      ].map(([rotulo, valor]) => (
                        <Grid item xs={12} sm={6} key={rotulo}>
                          <Typography variant="caption" color="text.secondary">
                            {rotulo}
                          </Typography>
                          <Typography>{valor}</Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>

                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6">Teste da conexão</Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    Envie uma mensagem manual para confirmar que o aparelho está operacional.
                  </Typography>
                  <Divider sx={{ my: 2.5 }} />

                  <Stack spacing={2}>
                    <TextField
                      label="Número com DDI e DDD *"
                      value={numeroTeste}
                      onChange={(event) => setNumeroTeste(event.target.value)}
                      placeholder="5511999999999"
                      helperText="Informe apenas um número que possua WhatsApp."
                      disabled={!aceiteUsoTerceiros || !conectado || acao !== ''}
                      inputProps={{ maxLength: 20 }}
                      fullWidth
                    />
                    <TextField
                      label="Mensagem de teste *"
                      value={mensagemTeste}
                      onChange={(event) => setMensagemTeste(event.target.value)}
                      disabled={!aceiteUsoTerceiros || !conectado || acao !== ''}
                      multiline
                      minRows={4}
                      inputProps={{ maxLength: 1000 }}
                      fullWidth
                    />
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Send />}
                      onClick={testar}
                      disabled={!aceiteUsoTerceiros || !conectado || acao !== ''}
                      sx={{ alignSelf: { md: 'flex-end' }, minWidth: 220 }}
                    >
                      {acao === 'testar' ? <CircularProgress size={22} color="inherit" /> :
                        'Enviar mensagem de teste'}
                    </Button>
                  </Stack>
                </Paper>
              </Stack>
            </Box>
          </Box>
        )}

        <Dialog
          open={confirmarAceiteAberto}
          onClose={() => acao === '' && setConfirmarAceiteAberto(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirmar ciência sobre o uso do WhatsApp</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mt: 1 }}>
              Ao confirmar, seu aceite será registrado no AuthSystem e um e-mail será enviado para
              <strong> SAOTech Softwares (saotechbr@gmail.com)</strong>.
            </Alert>
            <Typography mt={2}>
              O registro conterá data e hora, identificação do usuário, dados da empresa, tenant,
              endereço IP disponibilizado pela conexão ou proxy, navegador e demais dados técnicos
              disponíveis na requisição, para fins de comprovação da ciência destas condições.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setConfirmarAceiteAberto(false)}
              disabled={acao !== ''}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={confirmarAceiteUsoTerceiros}
              disabled={acao !== ''}
            >
              {acao === 'salvar_aceite'
                ? <CircularProgress size={22} color="inherit" />
                : 'Li, estou ciente e confirmo'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={Boolean(erro)}
          autoHideDuration={7000}
          onClose={() => setErro('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setErro('')}>{erro}</Alert>
        </Snackbar>
        <Snackbar
          open={Boolean(sucesso)}
          autoHideDuration={5000}
          onClose={() => setSucesso('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setSucesso('')}>{sucesso}</Alert>
        </Snackbar>
      </Box>
    </AcessoRestrito>
  );
}
